import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import Button from '@/components/inputs/button';
import { VARIANT as BUTTON_VARIANT } from '@/components/inputs/button/button.types';
import type { ShopNowButtonProperties } from './shop-now-button.types';
import usePartnerClick from '../../hooks/use-partner-click';
import trackEvent from '../../utils/track-event';
import {
  EVENT_NAME,
  USER_ACTION,
} from '../../utils/track-event/track-event.types';
import dataDogRum from '../../utils/data-dog-rum';
import { DD_ACTION } from '../../utils/data-dog-rum/data-dog-rum.types';
import { DATA_IDS } from '../../types';

const ShopNowButton = ({
  slug,
  isUserBlocked,
  merchantId,
  collinsonId = '',
  ctaLabel,
  ctaLabelTrack,
  collectionMethodType,
  setErrorCallback,
  className,
}: ShopNowButtonProperties) => {
  const { asPath } = useRouter();
  const [isTracked, setIsTracked] = useState<boolean>(false);
  const { data, isLoading, isError, handleClick, setIsError } = usePartnerClick(
    merchantId,
    collinsonId,
    slug,
  );

  useEffect(() => {
    if (data?.tracking_url) {
      setIsTracked(true);
    }
  }, [data]);

  useEffect(() => {
    setIsTracked(false);
  }, [slug]);

  useEffect(() => {
    if (setErrorCallback) setErrorCallback(isError);
  }, [setErrorCallback, isError]);

  const ctaHandler = async () => {
    if (isUserBlocked) return;
    if (collinsonId) {
      setIsError(false);
      await handleClick();
      trackEvent({
        event: EVENT_NAME.ONLINE_SHOP_NOW,
        retailerSlug: slug,
      });
      dataDogRum.addAction(DD_ACTION.SHOP_NOW, {
        slug,
        type: collectionMethodType,
      });
      return;
    }
    trackEvent({
      event: EVENT_NAME.ONLINE_LOGINBUTTON,
      user_action: USER_ACTION.RETAILER_LOG_IN,
    });
  };

  const label = isTracked ? ctaLabelTrack : ctaLabel;

  if (collinsonId) {
    return (
      <Button
        className={className}
        variant={BUTTON_VARIANT.Contained}
        onClick={() => ctaHandler()}
        isLoading={isLoading}
        disabled={isUserBlocked}
        title={label}
        dataId={DATA_IDS.SHOP_NOW}
        externalLink
      >
        {label}
      </Button>
    );
  }

  return (
    <Button
      as="link"
      className={className}
      variant={BUTTON_VARIANT.Contained}
      onClick={() => ctaHandler()}
      href={`/api/auth/login?returnTo=${encodeURIComponent(asPath)}`}
      isLoading={isLoading}
      title={label}
      dataId={DATA_IDS.SHOP_NOW}
    >
      {label}
    </Button>
  );
};

export default ShopNowButton;
